// BOM lists (Management › Records): the parts behind every order file. Upload the yard's STD lists workbook
// for a season (each "<MODEL> BOXES" sheet becomes that model's list: its standard sections and its option
// sections, with each part's SAP code, place, quantity and box) and the standard-equipment list (what a new
// order file of a model starts ticked). For each option section, the rule that brings it into a boat's BOM:
// one suggested from its heading ("Radar GARMIN" → RADAR and GARMIN ticked) until someone sets their own.
// Each model's sheet is also kept exactly as uploaded: Excel hands it back as it is, and a boat's BOM is that
// sheet with the sections it hasn't got deleted. Reading needs production:read; uploading and setting rules
// needs bom:manage.

import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BOM_RULE_FIELDS,
  MODEL_ORDER,
  ORDER_FILE_FIELDS,
  bomFieldLabel,
  bomLinkText,
  orderFileLabel,
  suggestBomLink,
  type BomCond,
  type BomCondOp,
  type BomItem,
  type BomLink,
  type BomPart,
  type BomSection,
} from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { fetchBomSheet, sheetTab, useBom, useBomList, useDeleteBomList, useSetBomLink, useSetStdEquipment, useUploadBom } from '../features/bom.js';
import { readStdEquipment, readStdLists, type BomSheetRead } from '../features/bomImport.js';
import { useNameResolver } from '../features/directory.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, PageHeader, SearchInput, useConfirm } from '../components/ui.js';
import { fold, hasText } from '../lib/search.js';
import { IconUpload } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';

export function BomLists() {
  const { can } = useAuth();
  const canManage = can('bom:manage');
  const { currentSeason, activeSeason, seasons } = useSeason();
  const [season, setSeason] = useState(currentSeason || activeSeason);
  const bomQ = useBom();
  const df = useDateFormatter();
  const nameOf = useNameResolver();
  const [confirmNode, confirm] = useConfirm();
  const upload = useUploadBom();
  const del = useDeleteBomList();
  const setStd = useSetStdEquipment();
  const listsIn = (bomQ.data?.lists ?? []).filter((l) => l.season === season);
  const [model, setModel] = useState('');
  const shown = model || listsIn[0]?.model || '';

  const listInput = useRef<HTMLInputElement>(null);
  const stdInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ file: File; sheets: BomSheetRead[] } | null>(null);
  const [stdPreview, setStdPreview] = useState<{ file: string; models: Array<{ model: string; items: string[]; fields: string[] }> } | null>(null);
  const [msg, setMsg] = useState('');

  const pickLists = async (f: File) => {
    setMsg('');
    try {
      const sheets = await readStdLists(f);
      if (!sheets.length) { setMsg(`${f.name} has no “<MODEL> BOXES” sheets to read.`); return; }
      setPreview({ file: f, sheets });
    } catch { setMsg(`${f.name} couldn’t be read as a workbook.`); }
  };
  const pickStd = async (f: File) => {
    setMsg('');
    try {
      const models = await readStdEquipment(f);
      if (!models.length) { setMsg(`${f.name} has no “<MODEL> STD List” blocks to read.`); return; }
      setStdPreview({ file: f.name, models });
    } catch { setMsg(`${f.name} couldn’t be read as a workbook.`); }
  };
  const saveLists = async () => {
    if (!preview) return;
    const p = preview;
    const res = await upload.mutateAsync({
      season,
      source: p.file.name,
      lists: p.sheets.map((s) => ({ model: s.model, sheet: s.sheet, sections: s.sections })),
      workbook: p.file,
      tabs: Object.fromEntries(p.sheets.map((s) => [s.model, s.tab])),
    });
    setPreview(null);
    setMsg(`Saved ${p.sheets.length} model lists for season ${season} from ${p.file.name}.${res.missed.length ? ` The sheet of the ${res.missed.join(', ')} couldn’t be kept, so ${res.missed.length === 1 ? 'its' : 'their'} BOMs can’t be made yet: upload the workbook again.` : ''}`);
  };
  const saveStd = async () => {
    if (!stdPreview) return;
    const p = stdPreview;
    await setStd.mutateAsync({ items: p.models.map((m) => ({ model: m.model, fields: m.fields })) });
    setStdPreview(null);
    setMsg(`Saved the standard equipment of ${p.models.length} models from ${p.file}.`);
  };

  return (
    <>
      <PageHeader title="BOM lists" meta={<span className="muted">The parts behind every order file</span>}>
        <label className="bl-season">
          <span className="muted">Season</span>
          <select className="field field-sm" value={season} onChange={(e) => { setSeason(e.target.value); setModel(''); }}>
            {[...new Set([...seasons, season])].filter(Boolean).sort().reverse().map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {canManage && (
          <>
            <input ref={listInput} type="file" accept=".xlsx,.xlsm,.xls" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pickLists(f); }} />
            <input ref={stdInput} type="file" accept=".xlsx,.xlsm,.xls" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pickStd(f); }} />
            <button className="btn btn-sm btn-primary" onClick={() => listInput.current?.click()}><IconUpload size={14} /> STD lists…</button>
            <button className="btn btn-sm" onClick={() => stdInput.current?.click()}><IconUpload size={14} /> Standard equipment…</button>
          </>
        )}
      </PageHeader>
      <div className="muted" role="status" style={msg ? { marginBottom: 10 } : undefined}>{msg}</div>
      {(upload.error ?? del.error ?? setStd.error) && <InlineError error={upload.error ?? del.error ?? setStd.error} onDismiss={() => { upload.reset(); del.reset(); setStd.reset(); }} />}
      {bomQ.isLoading && <LoadingState />}
      {bomQ.error && <ErrorState error={bomQ.error} onRetry={() => void bomQ.refetch()} />}

      {bomQ.data && !listsIn.length && (
        <EmptyState
          title={`No BOM lists for season ${season} yet`}
          hint={canManage ? 'Upload the STD lists workbook: each “<MODEL> BOXES” sheet becomes that model’s list.' : 'Someone who manages the BOM lists can upload the STD lists workbook.'}
        />
      )}

      {bomQ.data && listsIn.length > 0 && (
        <>
          <div className="toolbar bl-models" role="tablist" aria-label="Model">
            {MODEL_ORDER.filter((m) => listsIn.some((l) => l.model === m)).map((m) => (
              <button key={m} role="tab" aria-selected={shown === m} className={`btn btn-sm ${shown === m ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setModel(m)}>{m}</button>
            ))}
          </div>
          {listsIn.filter((l) => l.model === shown).map((l) => (
            <p key={l.model} className="muted bl-meta">
              {l.sheet} from {l.source || 'a workbook'} · {l.sections} sections · {l.items} parts · uploaded {df.dateTime(l.uploadedAt)} by {nameOf(l.uploadedBy)}
              {canManage && (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={async () => {
                    if (await confirm({ title: 'Remove this list', message: `Remove the ${l.model} list for season ${season}? Order files of the ${l.model} will use an older season’s list, if there is one.`, confirmLabel: 'Remove', danger: true })) {
                      del.mutate({ season, model: l.model });
                      setModel('');
                    }
                  }}
                >Remove</button>
              )}
            </p>
          ))}
          {shown && <ModelList season={season} model={shown} canManage={canManage} />}
        </>
      )}

      {preview && (
        <Modal
          title={`STD lists · season ${season}`}
          onClose={() => setPreview(null)}
          width={720}
          footer={(
            <>
              <button className="btn" onClick={() => setPreview(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={upload.isPending} onClick={() => void saveLists()}>{upload.isPending ? 'Saving…' : `Save ${preview.sheets.length} models`}</button>
            </>
          )}
        >
          <p className="muted">From {preview.file.name}. Saving replaces season {season}’s list for each of these models, and keeps each one’s sheet exactly as it is for its BOMs; rules already set stay with their sections.</p>
          <table className="tbl">
            <thead><tr><th>Model</th><th>Sheet</th><th className="num">Standard</th><th className="num">Options</th><th className="num">Parts</th><th>Notes</th></tr></thead>
            <tbody>
              {preview.sheets.map((s) => {
                const std = s.sections.filter((x) => x.part === 'std');
                const opt = s.sections.filter((x) => x.part === 'opt');
                return (
                  <tr key={s.model}>
                    <td><b>{s.model}</b></td>
                    <td>{s.sheet}</td>
                    <td className="num">{std.length}</td>
                    <td className="num">{opt.length}</td>
                    <td className="num">{s.sections.reduce((n, x) => n + x.items.length, 0)}</td>
                    <td className="muted">{s.warnings.join('; ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Modal>
      )}

      {stdPreview && (
        <Modal
          title="Standard equipment"
          onClose={() => setStdPreview(null)}
          width={720}
          footer={(
            <>
              <button className="btn" onClick={() => setStdPreview(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={setStd.isPending} onClick={() => void saveStd()}>{setStd.isPending ? 'Saving…' : `Save ${stdPreview.models.length} models`}</button>
            </>
          )}
        >
          <p className="muted">From {stdPreview.file}. A new order file of the model starts with these ticked; each can be changed on the model below.</p>
          {stdPreview.models.map((m) => (
            <div key={m.model} className="bl-std-prev">
              <b>{m.model}</b>
              <span className="muted">{m.items.join(' · ')}</span>
              <span>→ {m.fields.map(orderFileLabel).join(', ') || 'nothing on the order form'}</span>
            </div>
          ))}
        </Modal>
      )}
      {confirmNode}
    </>
  );
}

/** one model's list as the STD lists sheet has it: its standard equipment, then the standard table and the
 *  options table, every heading with its parts, a section's subsections under it (a standard heading that
 *  shares the code of the one before; an option section under its group), and each option's rule */
function ModelList({ season, model, canManage }: { season: string; model: string; canManage: boolean }) {
  const listQ = useBomList(season, model);
  const bomQ = useBom();
  const setLink = useSetBomLink();
  const setStd = useSetStdEquipment();
  const [editing, setEditing] = useState<BomSection | null>(null);
  const [stdOpen, setStdOpen] = useState(false);
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  const [q, setQ] = useState('');
  const [dl, setDl] = useState<{ busy: boolean; error: unknown }>({ busy: false, error: null });
  const links = bomQ.data?.config.links[model] ?? {};
  const std = bomQ.data?.config.std[model] ?? [];
  const sections = useMemo(() => listQ.data?.sections ?? [], [listQ.data]);
  const titles = useMemo(() => sections.filter((s) => s.part === 'opt').map((s) => s.title), [sections]);

  if (listQ.isLoading) return <LoadingState />;
  if (listQ.error) return <ErrorState error={listQ.error} onRetry={() => void listQ.refetch()} />;
  const opts = sections.filter((s) => s.part === 'opt');
  const manual = opts.filter((s) => (links[s.key] ?? suggestBomLink(s, titles)).mode === 'never').length;
  const needle = fold(q.trim());
  const toggle = (key: string) => setClosed((p) => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const ruleOf = (s: BomSection): ReactNode => {
    if (s.part === 'std') return null;
    const saved = links[s.key];
    const link: BomLink = saved ?? suggestBomLink(s, titles);
    return (
      <span className={`bl-rule${link.mode === 'never' ? ' is-manual' : ''}`}>
        {bomLinkText(link)}{!saved && link.mode !== 'never' && <span className="bl-sugg" title="Suggested from the heading; set it to keep your own"> suggested</span>}
        {canManage && <button className="btn btn-sm bl-rule-btn" onClick={(e) => { e.stopPropagation(); setEditing(s); }}>Rule…</button>}
      </span>
    );
  };
  // the model's sheet exactly as it was uploaded
  const onDownload = async () => {
    if (!listQ.data) return;
    setDl({ busy: true, error: null });
    try {
      const bytes = await fetchBomSheet(season, model);
      const { saveBlob, XLSX_TYPE } = await import('../features/orderFileExport.js');
      saveBlob(new Blob([bytes], { type: XLSX_TYPE }), `${sheetTab(listQ.data.sheet)} ${season}.xlsx`);
      setDl({ busy: false, error: null });
    } catch (error) {
      setDl({ busy: false, error });
    }
  };

  return (
    <>
      <section className="card bl-card">
        <div className="bl-card-head">
          <h2>Standard equipment</h2>
          {canManage && <button className="btn btn-sm" onClick={() => setStdOpen(true)}>Change…</button>}
        </div>
        <p className="bl-std">{std.length ? std.map(orderFileLabel).join(' · ') : <span className="muted">None set. A new {model} order file starts with nothing ticked.</span>}</p>
      </section>

      <div className="toolbar bl-bar">
        <SearchInput value={q} onChange={setQ} placeholder="Code, part, position or box" width={260} />
        <button className="btn btn-sm btn-ghost" onClick={() => setClosed(new Set())}>Expand all</button>
        <button className="btn btn-sm btn-ghost" onClick={() => setClosed(new Set(sections.map((s) => s.key)))}>Collapse all</button>
        <span className="osp-gap" />
        {manual > 0 && <span className="muted">{manual} option{manual === 1 ? '' : 's'} with no rule: ticked by hand on each order</span>}
        <button className="btn btn-sm" disabled={dl.busy} onClick={() => void onDownload()} title="The model’s sheet of the STD lists workbook, exactly as it was uploaded">{dl.busy ? 'Excel…' : 'Excel'}</button>
      </div>
      {dl.error != null && <InlineError error={dl.error} onDismiss={() => setDl({ busy: false, error: null })} />}

      <StdTable title="Standard" part="std" sections={sections} needle={needle} closed={closed} onToggle={toggle} ruleOf={ruleOf} />
      <StdTable title="Options" part="opt" sections={sections} needle={needle} closed={closed} onToggle={toggle} ruleOf={ruleOf} />

      {editing && (
        <RuleEditor
          section={editing}
          initial={links[editing.key] ?? suggestBomLink(editing, titles)}
          saved={Boolean(links[editing.key])}
          suggestion={suggestBomLink(editing, titles)}
          onClose={() => setEditing(null)}
          onSave={(link) => { setLink.mutate({ model, sectionKey: editing.key, link }); setEditing(null); }}
        />
      )}
      {stdOpen && (
        <StdEditor
          model={model}
          initial={std}
          onClose={() => setStdOpen(false)}
          onSave={(fields) => { setStd.mutate({ items: [{ model, fields }] }); setStdOpen(false); }}
        />
      )}
    </>
  );
}

const itemHit = (needle: string, i: BomItem) => hasText(needle, i.code, i.codeText, i.desc, i.pos, i.box, i.note);

/** one of the sheet's two tables, its columns as the STD lists have them */
function StdTable({ title, part, sections, needle, closed, onToggle, ruleOf }: {
  title: string; part: BomPart; sections: readonly BomSection[]; needle: string; closed: ReadonlySet<string>;
  onToggle: (key: string) => void; ruleOf: (s: BomSection) => ReactNode;
}) {
  const mine = sections.filter((s) => s.part === part);
  if (!mine.length) return null;
  const parts = mine.reduce((n, s) => n + s.items.length, 0);
  let group = '';
  let prevRef = '';
  const rows: ReactNode[] = [];
  for (const s of mine) {
    const items = needle ? s.items.filter((i) => itemHit(needle, i)) : s.items;
    const titleHit = needle ? hasText(needle, s.title, s.ref, s.group) : true;
    // a standard heading that shares the code of the one before is its subsection, as the sheet files them
    const sub = part === 'std' && Boolean(s.ref) && s.ref === prevRef;
    prevRef = s.ref;
    if (needle && !titleHit && !items.length) continue;
    if (part === 'opt' && s.group && s.group !== group) {
      group = s.group;
      rows.push(<tr key={`g:${s.key}`} className="bl-grp"><td colSpan={7}>{s.group}</td></tr>);
    }
    const open = !closed.has(s.key) || Boolean(needle);
    const rule = ruleOf(s);
    rows.push(
      <tr key={s.key} className={`bl-head${sub ? ' is-sub' : ''}${part === 'opt' && s.group ? ' in-grp' : ''}`} onClick={() => onToggle(s.key)} aria-expanded={open}>
        <td className="bl-c-ref">{s.ref}</td>
        <td colSpan={2} />
        <td className="bl-c-desc">
          <span className="bl-chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
          {sub && <span className="bl-sub" aria-hidden="true">↳ </span>}
          <b>{s.title}</b> <span className="muted">({s.items.length})</span>
          {s.note && <span className="muted"> · {s.note}</span>}
          {rule && <div className="bl-head-rule">{rule}</div>}
        </td>
        <td />
        <td />
        <td className="bl-c-box">{s.box}</td>
      </tr>,
    );
    if (!open) continue;
    (titleHit && needle ? s.items : items).forEach((i, n) => {
      rows.push(
        <tr key={`${s.key}:${n}`} className="bl-item">
          <td className="bl-c-ref muted">{s.ref}</td>
          <td className="mono">{i.code || <span className={i.codeText ? 'bl-badcode' : 'muted'} title={i.codeText ? 'Not a valid SAP code yet: left out of the SAP file' : 'No SAP code yet'}>{i.codeText || '-'}</span>}</td>
          <td className="mono">{i.pos}</td>
          <td className="bl-c-desc">{i.desc}</td>
          <td className="num">{i.qty ?? i.qtyText}</td>
          <td className="muted">{i.note}</td>
          <td className="bl-c-box">{i.box}</td>
        </tr>,
      );
    });
  }
  return (
    <section className="card bl-card bl-sheet">
      <div className="bl-card-head">
        <h2>{title} <span className="muted">· {mine.length} sections · {parts} parts{part === 'std' ? ' · always in' : ''}</span></h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl bl-stdtbl">
          <thead>
            <tr><th>Α/Α</th><th>ΚΩΔΙΚΟΣ SAP</th><th>ΘΕΣΗ ΑΘ</th><th>ΠΕΡΙΓΡΑΦΗ ΕΙΔΟΥΣ</th><th className="num">ΠΟΣΟΤΗΤΑ</th><th>NOTES</th><th>BOXES</th></tr>
          </thead>
          <tbody>
            {rows}
            {!rows.length && <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 16 }}>Nothing matches.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const OPS: Array<{ op: BomCondOp; label: string }> = [
  { op: 'on', label: 'is ticked' },
  { op: 'off', label: 'is not ticked' },
  { op: 'has', label: 'says' },
  { op: 'lacks', label: 'doesn’t say' },
];

/** a section's rule: always, only by hand, or when these order rows say so (all of them) */
function RuleEditor({ section, initial, saved, suggestion, onClose, onSave }: {
  section: BomSection; initial: BomLink; saved: boolean; suggestion: BomLink; onClose: () => void; onSave: (link: BomLink | null) => void;
}) {
  const [mode, setMode] = useState(initial.mode);
  const [conds, setConds] = useState<BomCond[]>(initial.conds.length ? initial.conds : [{ field: 'BA', op: 'on' }]);
  const setCond = (i: number, c: Partial<BomCond>) => setConds((p) => p.map((x, n) => (n === i ? { ...x, ...c } : x)));
  const valid = mode !== 'when' || conds.every((c) => c.field && (c.op === 'on' || c.op === 'off' || (c.text ?? '').trim()));
  return (
    <Modal
      title={section.title}
      dataTitle
      onClose={onClose}
      width={640}
      footer={(
        <>
          {saved && <button className="btn btn-ghost" style={{ marginRight: 'auto' }} onClick={() => onSave(null)} title={`Suggested: ${bomLinkText(suggestion)}`}>Use the suggestion</button>}
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid} onClick={() => onSave({ mode, conds: mode === 'when' ? conds.map((c) => (c.op === 'on' || c.op === 'off' ? { field: c.field, op: c.op } : { ...c, text: c.text?.trim() })) : [] })}>Save rule</button>
        </>
      )}
    >
      <p className="muted">{section.items.length} parts{section.group ? ` · ${section.group}` : ''}. Suggested from its heading: {bomLinkText(suggestion)}.</p>
      <div className="bl-modes" role="radiogroup" aria-label="Brought in">
        {([['when', 'When the order says'], ['always', 'Always'], ['never', 'Only when ticked by hand']] as const).map(([m, label]) => (
          <label key={m} className="check-row"><input type="radio" name="bl-mode" checked={mode === m} onChange={() => setMode(m)} /> {label}</label>
        ))}
      </div>
      {mode === 'when' && (
        <div className="bl-conds">
          {conds.map((c, i) => (
            <div key={i} className="bl-cond">
              <span className="muted bl-and">{i ? 'and' : ''}</span>
              <select className="field field-sm" aria-label="Order row" value={c.field} onChange={(e) => setCond(i, { field: e.target.value })}>
                {BOM_RULE_FIELDS.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
              </select>
              <select className="field field-sm" aria-label="Condition" value={c.op} onChange={(e) => setCond(i, { op: e.target.value as BomCondOp })}>
                {OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
              </select>
              {(c.op === 'has' || c.op === 'lacks') && <input className="field field-sm" aria-label="Words" value={c.text ?? ''} placeholder="e.g. V10" onChange={(e) => setCond(i, { text: e.target.value })} />}
              <button className="btn btn-sm btn-ghost" aria-label={`Remove ${bomFieldLabel(c.field)}`} disabled={conds.length < 2} onClick={() => setConds((p) => p.filter((_, n) => n !== i))}>✕</button>
            </div>
          ))}
          {conds.length < 12 && <button className="btn btn-sm btn-ghost" onClick={() => setConds((p) => [...p, { field: 'BA', op: 'on' }])}>+ And…</button>}
        </div>
      )}
    </Modal>
  );
}

/** a model's standard equipment: the order rows a new order file starts ticked */
function StdEditor({ model, initial, onClose, onSave }: { model: string; initial: string[]; onClose: () => void; onSave: (fields: string[]) => void }) {
  const [on, setOn] = useState(() => new Set(initial));
  const toggle = (k: string) => setOn((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <Modal
      title={`${model} standard equipment`}
      onClose={onClose}
      width={720}
      footer={(
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(ORDER_FILE_FIELDS.filter((f) => on.has(f.key)).map((f) => f.key))}>Save</button>
        </>
      )}
    >
      <div className="bl-stdgrid">
        {ORDER_FILE_FIELDS.filter((f) => f.kind === 'option').map((f) => (
          <label key={f.key} className="check-row"><input type="checkbox" checked={on.has(f.key)} onChange={() => toggle(f.key)} /> {orderFileLabel(f.key)}</label>
        ))}
      </div>
    </Modal>
  );
}
